import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { getCertificate } from "../../services/certificateService";
import { Certificate } from "../../types/certificate";
import QRCode from "qrcode";

export default function CertificateDetail() {
  const { certificateId } = useParams<{ certificateId: string }>();
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [loading, setLoading] = useState(true);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const certificateRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchCertificate = async () => {
      if (!certificateId) return;

      try {
        const data = await getCertificate(certificateId);
        setCertificate(data);

        if (data) {
          const verificationUrl = `${window.location.origin}/verify/${data.verificationCode}`;
          const qrUrl = await QRCode.toDataURL(verificationUrl, {
            width: 200,
            margin: 1,
          });
          setQrCodeUrl(qrUrl);
        }
      } catch (err) {
        console.error("Error fetching certificate:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCertificate();
  }, [certificateId]);

  const handleShare = async () => {
    if (!certificate) return;

    const shareUrl = `${window.location.origin}/certificates/${certificate.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `ใบเซอร์ติฟิเคต - ${certificate.courseTitle}`,
          text: `ฉันได้รับใบเซอร์ติฟิเคตจากคอร์ส ${certificate.courseTitle}!`,
          url: shareUrl,
        });
      } catch (err) {
        console.log("Share cancelled");
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = async () => {
    if (!certificateRef.current) return;

    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
      });

      const link = document.createElement("a");
      link.download = `certificate-${certificate?.certificateNumber}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (err) {
      console.error("Error downloading certificate:", err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-textLight">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-textDark">ไม่พบใบเซอร์ติฟิเคต</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-pink-50/20 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Actions */}
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/certificates"
            className="text-textLight hover:text-primary font-medium transition-colors"
          >
            ← กลับไปยังคลังใบเซอร์ติฟิเคต
          </Link>

          <div className="flex gap-3">
            <button
              onClick={handleShare}
              className="bg-white border-2 border-gray-200 text-textDark px-6 py-2 rounded-xl font-semibold hover:bg-gray-50 transition-all"
            >
              {copied ? "✓ คัดลอกแล้ว" : "🔗 แชร์"}
            </button>
            <button
              onClick={handleDownload}
              className="bg-gradient-primary text-white px-6 py-2 rounded-xl font-semibold hover:shadow-xl transition-all"
            >
              📥 ดาวน์โหลด
            </button>
          </div>
        </div>

        {/* Certificate */}
        <div
          ref={certificateRef}
          className="bg-white shadow-2xl relative overflow-hidden"
          style={{ aspectRatio: '1.414/1' }}
        >
          {/* Decorative Border Frame */}
          <div className="absolute inset-0">
            {/* Outer border */}
            <div className="absolute inset-0 border-[12px] border-cyan-600"></div>
            {/* Inner border */}
            <div className="absolute inset-6 border-[3px] border-cyan-500"></div>
            {/* Decorative corners */}
            <div className="absolute top-4 left-4 w-16 h-16 border-t-[6px] border-l-[6px] border-cyan-700"></div>
            <div className="absolute top-4 right-4 w-16 h-16 border-t-[6px] border-r-[6px] border-cyan-700"></div>
            <div className="absolute bottom-4 left-4 w-16 h-16 border-b-[6px] border-l-[6px] border-cyan-700"></div>
            <div className="absolute bottom-4 right-4 w-16 h-16 border-b-[6px] border-r-[6px] border-cyan-700"></div>
          </div>

          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, #000 10px, #000 11px)',
          }}></div>

          {/* Content */}
          <div className="relative z-10 text-center px-16 py-12 flex flex-col justify-between h-full">
            {/* Header */}
            <div>
              {/* Logo */}
              <div className="mb-6">
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-cyan-500 to-cyan-700 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white text-3xl font-bold">E</span>
                </div>
              </div>

              {/* Institution Name */}
              <h3 className="text-2xl font-bold text-cyan-700 mb-2 tracking-wider">EDUMI LEARNING PLATFORM</h3>
              <div className="w-32 h-[2px] bg-cyan-600 mx-auto mb-8"></div>

              {/* Title */}
              <h1 className="text-5xl font-serif font-bold text-gray-800 mb-3 tracking-wide">
                ใบประกาศนียบัตร
              </h1>
              <p className="text-xl text-gray-600 mb-10 italic">Certificate of Completion</p>
            </div>

            {/* Body */}
            <div className="flex-1 flex flex-col justify-center">
              {/* Recipient */}
              <p className="text-lg text-gray-600 mb-3">ขอมอบให้</p>
              <h2 className="text-4xl font-bold text-gray-900 mb-8 border-b-2 border-gray-300 pb-2 inline-block mx-auto px-8">
                {certificate.userName}
              </h2>

              {/* Achievement */}
              <p className="text-lg text-gray-600 mb-3">ที่ได้เรียนจบหลักสูตร</p>
              <h3 className="text-3xl font-bold text-cyan-700 mb-8 leading-relaxed">
                {certificate.courseTitle}
              </h3>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-8 max-w-2xl mx-auto mb-8">
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-2 uppercase tracking-wider">ผู้สอน</p>
                  <p className="font-semibold text-gray-800 text-lg">{certificate.instructorName}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-2 uppercase tracking-wider">วันที่สำเร็จการศึกษา</p>
                  <p className="font-semibold text-gray-800 text-lg">
                    {new Date(certificate.completionDate).toLocaleDateString("th-TH", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-end justify-between pt-8 border-t-2 border-gray-300">
              <div className="text-left">
                <div className="mb-4">
                  <div className="w-48 border-t-2 border-gray-800 mb-2"></div>
                  <p className="text-sm font-semibold text-gray-700">ผู้อำนวยการ EduMi</p>
                </div>
                <p className="text-xs text-gray-500">เลขที่ใบประกาศ</p>
                <p className="font-mono text-xs font-semibold text-gray-700">
                  {certificate.certificateNumber}
                </p>
              </div>

              {qrCodeUrl && (
                <div className="text-center">
                  <img src={qrCodeUrl} alt="QR Code" className="w-20 h-20 border-2 border-gray-300 p-1" />
                  <p className="text-xs text-gray-500 mt-1">ตรวจสอบความถูกต้อง</p>
                </div>
              )}

              <div className="text-right">
                <div className="mb-4">
                  <div className="w-48 border-t-2 border-gray-800 mb-2"></div>
                  <p className="text-sm font-semibold text-gray-700">{certificate.instructorName}</p>
                  <p className="text-xs text-gray-500">ผู้สอน</p>
                </div>
                <p className="text-xs text-gray-500">
                  ออกให้ ณ วันที่ {new Date(certificate.issueDate).toLocaleDateString("th-TH")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Verification Info */}
        <div className="mt-8 bg-white rounded-2xl shadow-lg p-6">
          <h3 className="font-bold text-textDark mb-4">🔐 ตรวจสอบความถูกต้อง</h3>
          <p className="text-sm text-textLight mb-4">
            ใบเซอร์ติฟิเคตนี้สามารถตรวจสอบความถูกต้องได้ผ่าน QR Code หรือเลขที่ใบเซอร์ติฟิเคต
          </p>
          <div className="flex gap-4">
            <Link
              to={`/verify/${certificate.verificationCode}`}
              className="text-primary font-semibold hover:underline"
            >
              ตรวจสอบใบเซอร์ติฟิเคต →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
